import { createComputed, createEffect, createMemo, createSignal, For, Index, Show } from "solid-js"
import { useParams } from "@solidjs/router"
import { v4 as uuid } from "uuid"
import { DataTypes, useLocalStore } from "@/context/StateContext"
import Types from "@/types"
import Name from "@/components/Name"
import getName from "@/utilities/name"
import CategorySelect from "./CategorySelect"
import LabeledInput from "./LabeledInput"

// Reusable name editor component
function NameEditor(props: {
  id: string
  fallback?: string
  class?: string
  onUpdate?: () => void
}) {
  const localStore = useLocalStore()
  const [name, setName] = createSignal('')
  const [nameId, setNameId] = createSignal<string | null>(null)
  const [isEditing, setIsEditing] = createSignal(false)

  // Load name from connection
  createEffect(async () => {
    if (!props.id || !localStore.database) return
    const { nameId, name } = await getName(props.id)
    setNameId(nameId)
    setName(name)
  })

  const updateName = async (newName: string) => {
    if (!nameId() || !localStore.database) return
    await localStore.updateConnection(nameId()!, newName)
    setIsEditing(false)
    props.onUpdate?.()
  }

  return (
    <div class={`flex flex-row items-center gap-2 cursor-pointer ${props.class}`}>
      <Show
        when={isEditing()}
        fallback={
          <button
            onClick={() => setIsEditing(true)}
            class="text-left hover:text-gray-600 dark:hover:text-gray-300"
          >
            {name() || props.fallback || 'Unnamed'}
          </button>
        }
      >
        <LabeledInput
          label=""
          value={name()}
          onInput={setName}
          onSubmit={() => updateName(name())}
          placeholder="Enter name..."
        />
        <button
          onClick={() => updateName(name())}
          class="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
        >
          Save
        </button>
        <button
          onClick={() => setIsEditing(false)}
          class="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
        >
          Cancel
        </button>
      </Show>
    </div>
  )
}

// CategoryItem component (internal to this file)
function CategoryItem(props: {
  id: string
  connections: Array<{
    type: string
    target: string
    origin: string
    id: string
  }>
  selectedProperties: string[]
}) {
  const filteredConnections = () => {
    return props.connections?.filter(conn => props.selectedProperties.includes(conn.type))
  }

  return (
    <div class="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <div class="dark:text-gray-100"><Name id={props.id} /></div>
        </div>
        <div class="flex flex-wrap gap-2">
          <For each={filteredConnections()}>{(connection) => (
            <span class="inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-700 px-2 py-1 text-xs text-gray-600 dark:text-gray-300">
              <Name id={connection.type} />: {connection.target}
            </span>
          )}</For>
        </div>
      </div>
    </div>
  )
}

// Types for section configuration
type SectionRule = {
  id: string
  type: string
  target: string
  sort?: {
    property: string
    direction: 'asc' | 'desc'
  }
  group?: {
    by: string
    sort?: {
      property: string
      direction: 'asc' | 'desc'
    }
  }
}

type Section = {
  id: string
  name: string
  rules: SectionRule[]
  sort?: {
    property: string
    direction: 'asc' | 'desc'
  }
  group?: {
    by: string
    sort?: {
      property: string
      direction: 'asc' | 'desc'
    }
  }
}

// Helper component for section rules
function SectionRuleEditor(props: {
  rule: SectionRule
  onUpdate: (rule: SectionRule) => void
  onDelete: () => void
}) {
  const [availableTargets, setAvailableTargets] = createSignal<any[]>([])
  const localStore = useLocalStore()

  // Load available targets based on rule type
  createEffect(async () => {
    if (!localStore.database) return
    const query = await localStore.database.connections.find({
      selector: {
        type: Types.Category,
        target: props.rule.type === 'category' ? Types.Category : Types.ConnectionType
      }
    }).exec()
    setAvailableTargets(query || [])
  })

  return (
    <div class="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800/50 rounded">
      <select
        value={props.rule.type}
        onChange={(e) => props.onUpdate({ ...props.rule, type: e.target.value, target: '' })}
        class="text-sm rounded border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
      >
        <option value="category">Category</option>
        <option value="property">Property</option>
      </select>
      <select
        value={props.rule.target}
        onChange={(e) => props.onUpdate({ ...props.rule, target: e.target.value })}
        class="flex-1 text-sm rounded border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
      >
        <option value="">Select target...</option>
        <For each={availableTargets()}>
          {(target) => (
            <option value={target.origin}>
              <Name id={target.origin} />
            </option>
          )}
        </For>
      </select>
      <button
        onClick={() => props.onDelete()}
        class="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

// Section component
function TableSection(props: {
  section: Section
  items: any[]
  onUpdateSection: (section: Section) => void
}) {
  const [isEditing, setIsEditing] = createSignal(false)
  const [sortProperty, setSortProperty] = createSignal(props.section.sort?.property || '')
  const [sortDirection, setSortDirection] = createSignal<'asc' | 'desc'>(props.section.sort?.direction || 'asc')
  const [groupBy, setGroupBy] = createSignal(props.section.group?.by || '')

  // Filter items based on section rules
  const filteredItems = createMemo(() => {
    return props.items.filter(item => {
      // Check each rule
      return props.section.rules.every(rule => {
        if (rule.type === 'category') {
          return item.target === rule.target
        }
        if (rule.type === 'property') {
          return item.connections?.some(conn => conn.type === rule.target)
        }
        return true
      })
    })
  })

  const sortedItems = createMemo(() => {
    let items = [...filteredItems()]
    if (sortProperty()) {
      items.sort((a, b) => {
        const aVal = a.connections?.find(c => c.type === sortProperty())?.target || ''
        const bVal = b.connections?.find(c => c.type === sortProperty())?.target || ''
        return sortDirection() === 'asc' ?
          String(aVal).localeCompare(String(bVal)) :
          String(bVal).localeCompare(String(aVal))
      })
    }
    return items
  })

  const groupedItems = createMemo(() => {
    if (!groupBy()) return { '': sortedItems() }

    return sortedItems().reduce((acc, item) => {
      const groupValue = item.connections?.find(c => c.type === groupBy())?.target || ''
      acc[groupValue] = acc[groupValue] || []
      acc[groupValue].push(item)
      return acc
    }, {} as Record<string, any[]>)
  })

  return (
    <details class="group" open>
      <summary class="flex items-center gap-4 p-2 cursor-pointer list-none bg-white dark:bg-gray-800 rounded-lg shadow">
        <div class="flex items-center gap-2">
          <svg class="w-4 h-4 transition-transform group-open:rotate-90" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
          <NameEditor
            id={props.section.id}
            fallback="New Section"
            class="font-medium"
          />
        </div>
        <button
          onClick={() => setIsEditing(v => !v)}
          class="ml-auto text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          {isEditing() ? 'Done' : 'Edit'}
        </button>
      </summary>

      <Show when={isEditing()}>
        <div class="mt-2 p-4 bg-white dark:bg-gray-800 rounded-lg shadow space-y-4">
          <div class="space-y-4">
            <div class="flex gap-2">
              <input
                type="text"
                value={props.section.name}
                onInput={(e) => props.onUpdateSection({ ...props.section, name: e.currentTarget.value })}
                class="flex-1 text-sm rounded border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                placeholder="Section name..."
              />
              <button
                onClick={() => setIsEditing(false)}
                class="px-3 py-1 text-sm bg-gray-900 dark:bg-gray-700 text-white rounded"
              >
                Done
              </button>
            </div>

            <div class="space-y-2">
              <label class="text-sm font-medium dark:text-gray-200">Rules</label>
              <For each={props.section.rules}>
                {(rule, index) => (
                  <SectionRuleEditor
                    rule={rule}
                    onUpdate={(updated) => {
                      const newRules = [...props.section.rules]
                      newRules[index()] = updated
                      props.onUpdateSection({ ...props.section, rules: newRules })
                    }}
                    onDelete={() => {
                      const newRules = props.section.rules.filter((_, i) => i !== index())
                      props.onUpdateSection({ ...props.section, rules: newRules })
                    }}
                  />
                )}
              </For>
              <button
                onClick={() => {
                  const newRule: SectionRule = {
                    id: uuid(),
                    type: 'category',
                    target: ''
                  }
                  props.onUpdateSection({
                    ...props.section,
                    rules: [...props.section.rules, newRule]
                  })
                }}
                class="w-full p-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border border-dashed border-gray-300 dark:border-gray-600 rounded"
              >
                Add Rule
              </button>
            </div>

            <div class="space-y-2">
              <label class="text-sm font-medium dark:text-gray-200">Sorting & Grouping</label>
              <div class="flex gap-2">
                <select
                  value={sortProperty()}
                  onChange={(e) => {
                    setSortProperty(e.target.value)
                    props.onUpdateSection({
                      ...props.section,
                      sort: {
                        property: e.target.value,
                        direction: sortDirection()
                      }
                    })
                  }}
                  class="text-sm rounded border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                >
                  <option value="">No sorting</option>
                  <For each={Object.keys(props.items[0] || {})}>
                    {(prop) => <option value={prop}>{prop}</option>}
                  </For>
                </select>
                <select
                  value={sortDirection()}
                  onChange={(e) => {
                    setSortDirection(e.target.value as 'asc' | 'desc')
                    props.onUpdateSection({
                      ...props.section,
                      sort: {
                        property: sortProperty(),
                        direction: e.target.value as 'asc' | 'desc'
                      }
                    })
                  }}
                  class="text-sm rounded border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
              <select
                value={groupBy()}
                onChange={(e) => {
                  setGroupBy(e.target.value)
                  props.onUpdateSection({
                    ...props.section,
                    group: e.target.value ? {
                      by: e.target.value
                    } : undefined
                  })
                }}
                class="w-full text-sm rounded border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
              >
                <option value="">No grouping</option>
                <For each={Object.keys(props.items[0] || {})}>
                  {(prop) => <option value={prop}>{prop}</option>}
                </For>
              </select>
            </div>
          </div>
        </div>
      </Show>

      <div class="mt-2 space-y-2">
        <For each={Object.entries(groupedItems())}>
          {([group, items]) => (
            <Show when={group} fallback={<For each={items}>{(item) => <CategoryItem {...item} />}</For>}>
              <details class="group ml-4" open>
                <summary class="flex items-center gap-2 p-2 cursor-pointer list-none bg-white dark:bg-gray-800 rounded-lg shadow">
                  <svg class="w-4 h-4 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                  </svg>
                  <span class="font-medium dark:text-gray-200">{group}</span>
                  <span class="ml-2 text-sm text-gray-500 dark:text-gray-400">({items.length})</span>
                </summary>
                <div class="mt-2 space-y-2 ml-4">
                  <For each={items}>{(item) => <CategoryItem {...item} />}</For>
                </div>
              </details>
            </Show>
          )}
        </For>
      </div>
    </details>
  )
}

export default function TablesDetailView() {
  const params = useParams()
  const [id, setId] = createSignal(params.id)
  createComputed(() => setId(params.id))

  /*
    Table header should ask for a category (origin: x, type: category, target: <category>) => display all origins
      option to create a new category with name (origin: x, type: category, target: category and origin: x, type: name, target: <name>)

    Rows can be added for properties (origin: column item, type: <property>, target: <property value>)
      option to create a new property with name (origin: y, type: name, target: <property name>)

    Customize which properties the header and body items show (image, name, description, etc.)
  */
  const localStore = useLocalStore()
  createEffect(async () => {
    if (!id() || !localStore.database) return
    // check if type: Types.Category, target: Types.TableCategory connection exists
    const query = localStore.database.connections.findOne({
      selector: {
        origin: id(),
        type: Types.Category,
        target: Types.Table
      }
    })
    const queryResult = await query.exec()
    if (!queryResult) {
      // create connection
      await localStore.createConnection({
        origin: id(),
        type: Types.Category,
        target: Types.Table,
        targetType: DataTypes.Reference
      })
    }
  })

  const [categories, setCategories] = createSignal<any[] | null>(null)
  createEffect(() => {
    if (!id() || !localStore.database) return
    // subscribe to changes
    const query = localStore.database.connections.find({
      selector: {
        type: Types.Category,
        target: Types.Category
      }
    })
    const querySubscription = query.$.subscribe(res => {
      setCategories(res || null)
    })
    return () => querySubscription.unsubscribe()
  })
  const [newCategoryName, setNewCategoryName] = createSignal('')

  const [activeCategory, setActiveCategory] = createSignal<string | null>(null)
  const [activeCategoryConnection, setActiveCategoryConnection] = createSignal<string | null>(null)
  const [activeCategoryName, setActiveCategoryName] = createSignal<string | null>(null)
  createEffect(async () => {
    if (!id() || !localStore.database) return
    // subscribe to changes
    const query = localStore.database.connections.findOne({
      selector: {
        origin: id(),
        type: Types.TableCategory,
      }
    })
    const querySubscription = query.$.subscribe(async res => {
      setActiveCategory(res?.target || null)
      setActiveCategoryConnection(res?.id || null)
      setActiveCategoryName((await getName(res?.target || '')).name)
    })
    return () => querySubscription.unsubscribe()
  })

  const [dropdownOpen, setDropdownOpen] = createSignal(false)
  const [isCreatingNew, setIsCreatingNew] = createSignal(false)

  const [categoryItems, setCategoryItems] = createSignal<any[] | null>(null)
  createEffect(() => {
    if (!activeCategory() || !localStore.database) return
    // subscribe to changes
    const query = localStore.database.connections.find({
      selector: {
        type: Types.Category,
        target: activeCategory()
      }
    })
    const querySubscription = query.$.subscribe(res => {
      setCategoryItems(res || null)
    })
    return () => querySubscription.unsubscribe()
  })
  const [newCategoryItemName, setNewCategoryItemName] = createSignal('')

  // New signals for connection types and filtering
  const [connectionTypes, setConnectionTypes] = createSignal<string[]>([])
  const [itemConnections, setItemConnections] = createSignal<Record<string, any[]>>({})

  const [selectedProperties, setSelectedProperties] = createSignal<string[]>([])
  const [newPropertyName, setNewPropertyName] = createSignal('')
  const [isAddingProperty, setIsAddingProperty] = createSignal(false)

  // Fetch and process connection types
  createEffect(async () => {
    if (!categoryItems() || !localStore.database) return

    const connections: Record<string, any[]> = {}
    const types = new Set<string>()

    for (const item of categoryItems() || []) {
      const query = localStore.database!.connections.find({
        selector: { origin: item.origin }
      })

      const itemConns = await query.exec()
      connections[item.origin] = itemConns

      // Add connection types to set
      itemConns.forEach(conn => types.add(conn.type))
    }

    setItemConnections(connections)
    const newTypes = Array.from(types)
    setConnectionTypes(newTypes)
    // Initialize with all properties selected
    if (selectedProperties().length === 0) {
      setSelectedProperties(newTypes)
    }
  })

  // Replace the connection type filter section with this new component
  function PropertySelector() {
    return (
      <div class="flex flex-col gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-medium text-gray-700 dark:text-gray-200">Properties</h3>
          <button
            onClick={() => setIsAddingProperty(true)}
            class="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          >
            <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Add property
          </button>
        </div>

        <Show when={isAddingProperty()}>
          <form class="flex gap-2" onSubmit={async (e) => {
            e.preventDefault()
            if (!newPropertyName()) return

            const newPropertyId = uuid()
            await localStore.createConnection({
              origin: newPropertyId,
              type: Types.Name,
              target: newPropertyName(),
              targetType: DataTypes.String
            })
            await localStore.createConnection({
              origin: newPropertyId,
              type: Types.Category,
              target: Types.ConnectionType,
              targetType: DataTypes.Reference
            })
            setNewPropertyName('')
            setIsAddingProperty(false)
            setSelectedProperties(prev => [...prev, newPropertyId])
          }}>
            <input
              type="text"
              value={newPropertyName()}
              onInput={(e: any) => setNewPropertyName(e.target.value)}
              class="flex h-9 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 text-sm dark:text-gray-300"
              placeholder="Property name..."
            />
            <button
              type="submit"
              class="inline-flex h-9 items-center rounded-md bg-gray-900 dark:bg-gray-700 px-3 text-sm font-medium text-white hover:bg-gray-800 dark:hover:bg-gray-600"
            >
              Add
            </button>
          </form>
        </Show>

        <div class="space-y-4">
          <Show when={connectionTypes().length > 0}>
            {/* Selected properties */}
            <div class="space-y-2">
              <For each={connectionTypes().filter(type => selectedProperties().includes(type))}>
                {(type) => (
                  <div class="flex items-center justify-between">
                    <label class="flex items-center">
                      <input
                        type="checkbox"
                        checked={true}
                        onChange={() => setSelectedProperties(prev => prev.filter(p => p !== type))}
                        class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                      />
                      <span class="ml-2 text-sm dark:text-gray-300"><Name id={type} /></span>
                    </label>
                  </div>
                )}
              </For>
            </div>

            {/* Available properties */}
            <div class="space-y-2 pt-4 border-t border-gray-100 dark:border-gray-700">
              <For each={connectionTypes().filter(type => !selectedProperties().includes(type))}>
                {(type) => (
                  <div class="flex items-center justify-between">
                    <label class="flex items-center">
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={() => setSelectedProperties(prev => [...prev, type])}
                        class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                      />
                      <span class="ml-2 text-sm dark:text-gray-300"><Name id={type} /></span>
                    </label>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </div>
      </div>
    )
  }

  const [sections, setSections] = createSignal<Section[]>([])

  createEffect(() => {
    if (!id() || !localStore.database) return

    const sectionQuery = localStore.database.connections.find({
      selector: {
        origin: id(),
        type: Types.TableSection
      }
    })

    const subscription = sectionQuery.$.subscribe(async (sectionRefs) => {
      if (!sectionRefs) {
        setSections([])
        return
      }

      const loadedSections: Section[] = []

      for (const ref of sectionRefs) {
        const section: Section = {
          id: ref.target,
          name: (await getName(ref.target)).name || 'Unnamed Section',
          rules: await loadSectionRules(ref.target),
          sort: await loadSectionSort(ref.target),
          group: await loadSectionGroup(ref.target)
        }
        loadedSections.push(section)
      }

      setSections(loadedSections)
    })

    return () => subscription.unsubscribe()
  })

  // Helper functions for section loading
  async function loadSectionRules(sectionId: string) {
    const rulesQuery = await localStore.database!.connections.find({
      selector: {
        origin: sectionId,
        type: Types.SectionRule
      }
    }).exec()

    return rulesQuery.map(rule => ({
      id: rule.id,
      type: rule.target === Types.Category ? 'category' : 'property',
      target: rule.targetType
    }))
  }

  async function loadSectionSort(sectionId: string) {
    const sortQuery = await localStore.database!.connections.findOne({
      selector: {
        origin: sectionId,
        type: Types.SectionSort
      }
    }).exec()

    return sortQuery ? {
      property: sortQuery.target,
      direction: sortQuery.targetType as 'asc' | 'desc'
    } : undefined
  }

  async function loadSectionGroup(sectionId: string) {
    const groupQuery = await localStore.database!.connections.findOne({
      selector: {
        origin: sectionId,
        type: Types.SectionGroup
      }
    }).exec()

    return groupQuery ? {
      by: groupQuery.target,
      sort: groupQuery.targetType ? {
        property: groupQuery.targetType,
        direction: groupQuery.data?.direction || 'asc'
      } : undefined
    } : undefined
  }

  // Modified section management
  const addSection = async () => {
    const sectionId = uuid()

    // Create section reference
    await localStore.createConnection({
      origin: id(),
      type: Types.TableSection,
      target: sectionId,
      targetType: DataTypes.Reference
    })

    // Add default name
    await localStore.createConnection({
      origin: sectionId,
      type: Types.Name,
      target: 'New Section',
      targetType: DataTypes.String
    })
  }

  const updateSection = async (section: Section) => {
    // Update name if changed
    const currentName = (await getName(section.id)).name
    if (currentName !== section.name) {
      const nameQuery = await localStore.database!.connections.findOne({
        selector: {
          origin: section.id,
          type: Types.Name
        }
      }).exec()

      if (nameQuery) {
        await localStore.updateConnection(nameQuery.id, section.name)
      } else {
        await localStore.createConnection({
          origin: section.id,
          type: Types.Name,
          target: section.name,
          targetType: DataTypes.String
        })
      }
    }

    // Update rules
    const existingRules = await localStore.database!.connections.find({
      selector: {
        origin: section.id,
        type: Types.SectionRule
      }
    }).exec()

    // Delete removed rules
    for (const rule of existingRules) {
      if (!section.rules.find(r => r.id === rule.id)) {
        await localStore.database!.connections.findOne(rule.id).remove()
      }
    }

    // Update/create rules
    for (const rule of section.rules) {
      const existing = existingRules.find(r => r.id === rule.id)
      if (existing) {
        await localStore.updateConnection(existing.id, rule.target)
      } else {
        await localStore.createConnection({
          origin: section.id,
          type: Types.SectionRule,
          target: rule.type,
          targetType: rule.target
        })
      }
    }

    // Update sort settings
    const sortQuery = await localStore.database!.connections.findOne({
      selector: {
        origin: section.id,
        type: Types.SectionSort
      }
    }).exec()

    if (section.sort) {
      if (sortQuery) {
        await localStore.updateConnection(sortQuery.id, section.sort.property, section.sort.direction)
      } else {
        await localStore.createConnection({
          origin: section.id,
          type: Types.SectionSort,
          target: section.sort.property,
          targetType: section.sort.direction
        })
      }
    } else if (sortQuery) {
      await localStore.database!.connections.findOne(sortQuery.id).remove()
    }

    // Update group settings
    const groupQuery = await localStore.database!.connections.findOne({
      selector: {
        origin: section.id,
        type: Types.SectionGroup
      }
    }).exec()

    if (section.group) {
      if (groupQuery) {
        await localStore.updateConnection(
          groupQuery.id,
          section.group.by,
          section.group.sort?.property || null,
          { direction: section.group.sort?.direction }
        )
      } else {
        await localStore.createConnection({
          origin: section.id,
          type: Types.SectionGroup,
          target: section.group.by,
          targetType: section.group.sort?.property || null,
          data: section.group.sort ? { direction: section.group.sort.direction } : undefined
        })
      }
    } else if (groupQuery) {
      await localStore.database!.connections.findOne(groupQuery.id).remove()
    }
  }

  const updateCategory = async (categoryId: string) => {
    if (activeCategoryConnection()) {
      await localStore.updateConnection(activeCategoryConnection()!, categoryId)
    } else {
      await localStore.createConnection({
        origin: id(),
        type: Types.TableCategory,
        target: categoryId,
        targetType: DataTypes.Reference
      })
    }
  }

  return (
    <div class="flex flex-col h-full p-6 max-w-7xl mx-auto w-full space-y-6">
      <Show when={id()} keyed>
        {/* Table name */}
        <NameEditor
          id={id()}
          fallback="New Table"
          class="text-2xl font-medium dark:text-gray-200"
        />

        {/* Category selector */}
        <CategorySelect
          value={activeCategory()}
          onChange={updateCategory}
          categories={categories()}
        />

        <Show when={activeCategory()} fallback={
          <p class="text-sm text-gray-500 dark:text-gray-400">Select a category</p>
        }>
          {/* Table */}
          <div class="flex flex-col gap-4">
            {/* Table header */}
            <div class="flex flex-col gap-6">
              {/* Add new item for selected category */}

            </div>

            {/* Replace the old connection type filter with PropertySelector */}
            <div class="flex gap-4">
              <div class="flex-1">
                {/* Category items grid */}
                <div class="flex flex-row gap-4 w-full overflow-x-auto">
                  <Show when={categoryItems()} fallback={
                    <p class="text-sm text-gray-500 dark:text-gray-400">No items found</p>
                  }>
                    <For each={categoryItems()}>{(item: any) => {
                      const connections = itemConnections()[item.origin] || []
                      return (
                        <CategoryItem
                          id={item.origin}
                          connections={connections}
                          selectedProperties={selectedProperties()}
                        />
                      )
                    }}</For>
                  </Show>
                </div>
              </div>
              <div class="w-64 flex-shrink-0">
                <PropertySelector />
              </div>
            </div>

            <div class="flex justify-between items-center">
              <h2 class="text-lg font-medium dark:text-gray-200">Sections</h2>
              <button
                onClick={addSection}
                class="px-3 py-1 text-sm bg-gray-900 dark:bg-gray-700 text-white rounded"
              >
                Add Section
              </button>
            </div>

            <div class="space-y-4">
              <For each={sections()}>
                {(section) => (
                  <TableSection
                    section={section}
                    items={categoryItems() || []}
                    onUpdateSection={updateSection}
                  />
                )}
              </For>
            </div>
          </div>
        </Show>
      </Show>
    </div>
  )
}
